import React, { useEffect, useMemo, useState } from "react";
import MobilePage, { PageHeader } from "../../../Components/MobilePage";
import { Trans, useTranslation } from "react-i18next";

import MDContainer from "@material-ui/core/Container";
import { CardContent } from "@material-ui/core";
import Card from "@material-ui/core/Card";
import EpmsImage from "../../../../components/Common/EpmsImage";
import Button from "@material-ui/core/Button";
import CardActions from "@material-ui/core/CardActions";
import makeStyles from "@material-ui/core/styles/makeStyles";
import { Link, useRouteMatch } from "react-router-dom";
import { usePictureSet } from "../../hooks/usePictureSet";
import AppBar from "../../Components/AppBar";
import {
  PRODUCT_DIGITAL_FILE_UUID,
  PRODUCT_DIGITAL_GROUP_FILE_UUID,
  PRODUCT_DIGITAL_MINI_GROUP_FILE_UUID,
  PRODUCT_DIGITAL_SET_UUID,
  usePriceList,
} from "../../../providers/PriceListProvider";
import { generatePath } from "react-router";
import useRoutes from "../../../hooks/useRoutes";
import {
  Popup,
  PopupActions,
  PopupButton,
  PopupHeading,
} from "../../../../components/Common/Popup";
import Box from "@material-ui/core/Box";
import { useAPI } from "../../../../lib/common";

const useStyles = makeStyles((theme) => ({
  card: {
    marginBottom: "8px",
  },
  cardContent: {
    padding: 0,
    "& img": {
      width: "100%",
    },
  },
  downloadImage: {
    height: "100%",
    maxHeight: "40vh",
    minHeight: 0,
    backgroundPosition: "center center",
    backgroundSize: "cover",
    backgroundRepeat: "no-repeat",
  },
  backgroundList: {
    listStyle: "none",
    //backgroundColor: 'red',
    margin: 0,
    padding: 0,
    maxWidth: "100%",
    overflowX: "auto",
    display: "inline-flex",
    boxSizing: "border-box",
    "& > li:first-child": {
      marginLeft: theme.spacing(2),
    },
    "& > li": {
      display: "flex",
      flexDirection: "column",
      minHeight: 0,
      maxHeight: "100%",
      //backgroundColor: 'blue',
      "& > div": {
        textAlign: "center",
        fontSize: "14px",
        flexBasis: "40px",
      },
      "& img": {
        minHeight: 0,
        height: "130px",
        outline: "1px solid #B5B5B580",
        margin: "4px",
        boxSizing: "border-box",
      },
      "&$selectedBackground": {
        "& img": {
          outline: `4px solid ${theme.palette.secondary.main}`,
        },
      },
      "&$selectedBackground $transparentBackground": {
        outline: `4px solid ${theme.palette.secondary.main}`,
      },
      "& $transparentBackground": {
        width: "100px",
        height: "130px",
        margin: "4px",
        flexGrow: 1,
      },
    },
  },
  description: {},
  selectedBackground: {},
  transparentBackground: {
    backgroundColor: "white",
    backgroundImage:
      "linear-gradient(45deg, #eee 25%, transparent 25%), linear-gradient(-45deg, #eee 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #eee 75%), linear-gradient(-45deg, transparent 75%, #eee 75%)",
    backgroundSize: "20px 20px",
    backgroundPosition: "0 0, 0 10px, 10px -10px, -10px 0px",
    backgroundRepeat: "repeat",
  },
}));

export default () => {
  const { params } = useRouteMatch();
  const { notFound, pictureSet } = usePictureSet(params.pictureSetUuid);
  const { getProductByUuid } = usePriceList();
  const routes = useRoutes();
  const { t } = useTranslation();
  const classes = useStyles();

  const digitalPortraitProduct = getProductByUuid(PRODUCT_DIGITAL_FILE_UUID);
  const digitalMiniGroupProduct = getProductByUuid(
    PRODUCT_DIGITAL_MINI_GROUP_FILE_UUID,
  );
  const digitalGroupProduct = getProductByUuid(
    PRODUCT_DIGITAL_GROUP_FILE_UUID,
  );
  const digitalPortraitSetProduct = getProductByUuid(PRODUCT_DIGITAL_SET_UUID);

  const boughtDigitalCopies = useMemo(() => {
    if (!pictureSet) {
      return false;
    }

    if (pictureSet.bought || pictureSet.inCart) {
      return true;
    }

    const boughtPictures = pictureSet.pictures.filter(
      (picture) => picture.bought || picture.inCart,
    );

    return boughtPictures.length > 0;
  }, [pictureSet]);

  const buyLink = generatePath(
    routes["products.digitalportraits.subject"].path,
    { subjectUuid: params.pictureSetUuid },
  );

  if (!pictureSet) {
    return <Trans>Loading</Trans>;
  }

  return (
    <MobilePage requireUser>
      <AppBar />
      <PageHeader>
        <Trans>Picture set {{ subjectName: pictureSet.subject.name }}</Trans>
      </PageHeader>
      <MDContainer>
        {pictureSet.pictures.map((picture) => {
          let product = null;
          if (picture.pictureType == "IP") {
            product = digitalPortraitProduct || digitalPortraitSetProduct;
          } else if (picture.pictureType == "MGP") {
            product = digitalMiniGroupProduct;
          } else if (picture.pictureType == "GP") {
            product = digitalGroupProduct;
          }
          return (
            <Card className={classes.card}>
              <CardContent className={classes.cardContent}>
                <EpmsImage uuid={picture.uuid} />
              </CardContent>
              {(boughtDigitalCopies || product) && (
                <PictureSetPictureActions
                  pictureSet={pictureSet}
                  picture={picture}
                  product={product}
                  buyLink={buyLink}
                />
              )}
            </Card>
          );
        })}
        {pictureSet.pictures.length == 0 && (
          <>{getNoImageReason(pictureSet, t)}</>
        )}
      </MDContainer>
    </MobilePage>
  );
};

function getNoImageReason(imageSet, t) {
  if (imageSet.job.hasBeenInWebShop || imageSet.job.requireFineTunedPictures) {
    return (
      <p>
        {t(
          "Your pictures are not yet available in the webhop. You will receive an email from us with information as soon as the pictures are ready!",
        )}
      </p>
    );
  }
  // Unknown reason :/
  return (
    <p>
      <Trans>No pictures currently available</Trans>
    </p>
  );
}

const PictureSetPictureActions = ({
  pictureSet,
  picture,
  buyLink,
  product,
}) => {
  const [chooseBackground, setChooseBackground] = useState(null);
  const routes = useRoutes();
  const [t] = useTranslation();

  console.log("Picture set actions!");

  const downloadFile = (picture) => {
    if (picture.greenscreen) {
      setChooseBackground(picture);
    } else {
      const link = generatePath(routes["pictureset.download.file"].path, {
        uuid: picture.uuid,
      });
      document.location.href = link;
    }
  };

  let fileCanBeBought = product && !picture.bought && !picture.inCart;

  if (picture.pictureType === "IP") {
    if (pictureSet.bought || pictureSet.inCart) {
      fileCanBeBought = false;
    }
  }

  return (
    <>
      <CardActions>
        {((pictureSet.bought && picture.pictureType == "IP") ||
          picture.bought) && (
          <Button
            size="small"
            component={Link}
            color="primary"
            onClick={() => downloadFile(picture)}
            to={`/my-account/picture-sets/${pictureSet.subject.uuid}`}
          >
            <Trans>Download</Trans>
          </Button>
        )}
        {fileCanBeBought && (
          <Button size="small" component={Link} color="primary" to={buyLink}>
            {picture.pictureType == "IP" ? (
              <Trans>Buy digital portraits</Trans>
            ) : (
              <Trans>Buy digital picture</Trans>
            )}
          </Button>
        )}
        {pictureSet.inCart && picture.pictureType === "IP" && (
          <Button size="small" component={Link} color="primary" to={buyLink}>
            <Trans>Picture set in cart</Trans>
          </Button>
        )}
        {picture.inCart && (
          <Button size="small" component={Link} color="primary" to={buyLink}>
            <Trans>Picture in cart</Trans>
          </Button>
        )}
      </CardActions>
      {chooseBackground && (
        <ChoosePictureBackgroundDialog
          picture={chooseBackground}
          onClose={() => setChooseBackground(null)}
        />
      )}
    </>
  );
};

const ChoosePictureBackgroundDialog = ({ picture, onClose }) => {
  const [backgrounds, isLoading] = useAPI(
    "/ajax/template/get_image_backgrounds.php?uuid=" + picture.uuid,
  );
  const classes = useStyles();
  const [currentBackground, setCurrentBackground] = useState();
  const routes = useRoutes();
  const [t] = useTranslation();

  const downloadFile = () => {
    let background = "transparent";
    if (currentBackground != "transparent") {
      background = currentBackground.uuid;
    }

    const link = generatePath(
      routes["pictureset.download.file.background"].path,
      { uuid: picture.uuid, background: background },
    );

    // Can we close self before redirect?
    onClose();

    document.location.href = link;
  };

  const [defaultBackground, bgs] = useMemo(() => {
    if (!backgrounds) {
      return [null, []];
    }
    let bg = backgrounds.find((bg) => bg.default);
    if (!bg && backgrounds.length > 0) {
      bg = backgrounds[0];
    }
    const rest = backgrounds.filter((b) => b.uuid != bg.uuid);
    return [bg, rest];
  }, [backgrounds]);

  useEffect(() => {
    if (!backgrounds) {
      return;
    }
    if (defaultBackground) {
      setCurrentBackground(defaultBackground);
    }
  }, [backgrounds]);

  let styles = {};
  if (currentBackground && currentBackground != "transparent") {
    styles = {
      backgroundImage: `url(${currentBackground.src}`,
    };
  }

  return (
    <Popup fullScreen onClose={onClose}>
      <PopupHeading>
        <Trans>Select picture background</Trans>
      </PopupHeading>
      <Box
        display="flex"
        flexDirection="column"
        flexGrow={1}
        sx={{ minHeight: 0, maxWidth: "100vw" }}
      >
        <Box
          flex={1}
          sx={{ maxWidth: "100%", maxHeight: "60%" }}
          mb={2}
          display="flex"
          justifyContent="center"
        >
          <EpmsImage
            uuid={picture.uuid}
            className={`${classes.downloadImage} ${
              currentBackground == "transparent"
                ? classes.transparentBackground
                : ""
            }`}
            style={styles}
          />
        </Box>
        <ul className={classes.backgroundList}>
          {defaultBackground && (
            <li
              onClick={() => setCurrentBackground(defaultBackground)}
              className={
                defaultBackground == currentBackground
                  ? classes.selectedBackground
                  : ""
              }
            >
              <BackgroundPreview background={defaultBackground} />
              <div className={classes.description}>
                <Trans>Default background</Trans>
              </div>
            </li>
          )}
          <li
            onClick={() => setCurrentBackground("transparent")}
            className={
              "transparent" == currentBackground
                ? classes.selectedBackground
                : ""
            }
          >
            <BackgroundTransparentPreview />
            <div className={classes.description}>
              <Trans>Transparent</Trans>
            </div>
          </li>
          {bgs.map((bg) => {
            return (
              <li
                key={bg.uuid}
                onClick={() => setCurrentBackground(bg)}
                className={
                  bg == currentBackground ? classes.selectedBackground : ""
                }
              >
                <BackgroundPreview background={bg} />
                <div>&nbsp;</div>
              </li>
            );
          })}
        </ul>
      </Box>
      <PopupActions>
        <PopupButton secondary onClick={onClose}>
          <Trans>Cancel</Trans>
        </PopupButton>
        <PopupButton onClick={downloadFile}>
          <Trans>Download</Trans>
        </PopupButton>
      </PopupActions>
    </Popup>
  );
};

const BackgroundPreview = ({ background }) => {
  return <img src={background.src} />;
};

const BackgroundTransparentPreview = () => {
  const classes = useStyles();
  return <div className={classes.transparentBackground} />;
};
